import React, { FC, useContext, useEffect, useRef, useState } from 'react'
import './UserNamePasswordForm.scss'
import { Button, Form, FormInput, Icon, Link } from '@connectcore/connect-components-react'
import {
  ENVIRONMENT_VARIABLES,
  FORGOT_PASSWORD_ROUTE,
  FORGOT_USERNAME_ROUTE,
  LOGIN_FAILURE_SCD_ERROR,
  LOGIN_FORM_HEADER,
  PASSWORD_REQUIRED_MESSAGE,
  SELF_ENROLL_URL,
  USERNAME_REQUIRED_MESSAGE,
} from '~utils/constants'
import {
  ALIGN_BUTTONS_AND_LINKS,
  FORGOT_LINKS_LAYOUT,
  SHOW_FORGOT_PASSWORD_LINK,
  SHOW_FORGOT_USERNAME_LINK,
  SHOW_SIGNIN_AND_ENROLL_TAB,
} from '~utils/featureFlags'
import { IUserContextType, UserContext } from '~utils/context/UserContext'
import { useHistory } from 'react-router-dom'
import { CallbackType } from '@forgerock/javascript-sdk'
import { getErrorTemplate } from '~utils/helpers'

interface IFormState {
  formData: { userName: string | null; password: string | null }
  feedback: { userName: string | null; password: string | null }
}

const UserNamePasswordForm: FC = () => {
  const [formState, setFormState] = useState<IFormState>({
    formData: { userName: null, password: null },
    feedback: { userName: null, password: null },
  })
  const [showPass, setShowPass] = useState(false)
  const [ariaValue, setariaValue] = useState(false)
  const { step, nextStep, showErr, storeToken, setStep, setIsLoading, userInputError, env } =
    useContext(UserContext) as IUserContextType
  const [error, setError] = useState(null)
  const [apiError, setApiError] = useState(false)
  const history = useHistory()

  // const pingFormRefval = useRef<HTMLInputElement>(null)
  let userNameCallBackFn
  let passwordCallBackFn

  const loginUsernameRef = useRef<any>(null)
  const validateAndSetFormData = e => {
    setApiError(false)
    let feedback = {}
    setError(null) // Clear existing errors from the form
    if (Object.keys(e).includes('userName') && !e?.userName?.trim()) {
      feedback = { userName: USERNAME_REQUIRED_MESSAGE }
    } else if (Object.keys(e).includes('userName') && e.userName) {
      feedback = { userName: null }
    }

    if (Object.keys(e).includes('password') && !e?.password?.trim()) {
      feedback = { password: PASSWORD_REQUIRED_MESSAGE }
    } else if (Object.keys(e).includes('password') && e?.password) {
      feedback = { password: null }
    }

    setFormState({
      ...formState,
      feedback: { ...formState.feedback, ...feedback },
      formData: { ...formState.formData, ...e },
    })
  }

  if (step?.getCallbacksOfType(CallbackType.NameCallback)?.length > 0)
    userNameCallBackFn = step?.getCallbackOfType(CallbackType.NameCallback)

  if (step?.getCallbacksOfType(CallbackType.PasswordCallback)?.length > 0)
    passwordCallBackFn = step?.getCallbackOfType(CallbackType.PasswordCallback)

  const submitUserNamePassword = () => {
    const userName = formState.formData.userName?.trim()
    const password = formState.formData.password?.trim()
    setariaValue(true)
    loginUsernameRef.current.focus()
    if (userName && password) {
      userNameCallBackFn?.setInputValue(userName)
      passwordCallBackFn?.setInputValue(password)
      nextStep(step)
    } else {
      const feedback = {
        userName: userName ? null : USERNAME_REQUIRED_MESSAGE,
        password: password ? null : PASSWORD_REQUIRED_MESSAGE,
      }
      setFormState({ ...formState, feedback: { ...formState.feedback, ...feedback } })
    }
  }

  useEffect(() => {
    setError(userInputError)
  }, [userInputError])

  useEffect(() => {
    setApiError(showErr)
  }, [showErr])

  const navigateToPage = () => {
    window.open(ENVIRONMENT_VARIABLES[env][SELF_ENROLL_URL], '_blank')
  }

  return (
    <Form
      className="UserNamePasswordForm h-100"
      data-testid="UserNamePasswordForm"
      onChange={e => validateAndSetFormData(e)}
      onSubmit={() => submitUserNamePassword()}
      key="UserNamePasswordForm"
    >
      {SHOW_SIGNIN_AND_ENROLL_TAB && (
        <div className="align redirect-links">
          <Button kind="primary" name="signIn" className="my-3 col-3 br-0">
            Sign In
          </Button>
          <Button
            kind="secondary"
            name="enroll"
            className="my-3 col-3 br-0"
            onClick={navigateToPage}
          >
            Enroll
          </Button>
        </div>
      )}

      <div className="form-section">
        <h2 className="my-5 fw-bold">{LOGIN_FORM_HEADER}</h2>

        <div>
          <FormInput
            name="userName"
            required={true}
            label="Username"
            innerRef={loginUsernameRef}
            aria-invalid={ariaValue}
            feedback={<p role="alert">{formState.feedback.userName}</p>}
            validationState={
              formState.feedback.userName !== null && formState.feedback.userName?.length > 0
                ? 'invalid'
                : undefined
            }
          />
        </div>

        <div>
          <FormInput
            className="my-5"
            name="password"
            required={true}
            label="Password"
            iconOverlay
            type={showPass ? 'text' : 'password'}
            feedback={<p role="alert">{formState.feedback.password}</p>}
            validationState={
              formState.feedback.password !== null && formState.feedback.password?.length > 0
                ? 'invalid'
                : undefined
            }
            appendWith={
              <Icon
                className="p-0"
                name={showPass ? 'hide' : 'view-watchlist'}
                actionable
                onClick={() => {
                  setShowPass(!showPass)
                }}
                aria-label={showPass ? 'Hide password' : 'Show password'}
              />
            }
          />
        </div>

        {apiError && (
          <p className="invalid-feedback" role="alert">
            {LOGIN_FAILURE_SCD_ERROR}
          </p>
        )}
        <p className="invalid-feedback" role="alert">
          {getErrorTemplate(error)}
        </p>

        <div className="d-flex flex-column">
          <Button kind="primary" className={`my-3 align-${ALIGN_BUTTONS_AND_LINKS}`}>
            Login
          </Button>

          <div
            className={`forgot-links ${
              ALIGN_BUTTONS_AND_LINKS === 'center'
                ? 'align-self-center'
                : ALIGN_BUTTONS_AND_LINKS === 'right'
                ? 'align-self-end'
                : 'align-self-start'
            }
             ${FORGOT_LINKS_LAYOUT === 'stack' ? 'hstack gap-3' : ''}`}
          >
            {SHOW_FORGOT_PASSWORD_LINK && (
              <Link
                href="#"
                className="my-3 align-right"
                onClick={eve => {
                  eve.preventDefault()
                  setIsLoading(true)
                  setStep(undefined)
                  history.push({
                    pathname: FORGOT_PASSWORD_ROUTE,
                    state: { storeToken: storeToken },
                  })
                }}
              >
                Forgot Password
              </Link>
            )}
            <br />

            {SHOW_FORGOT_USERNAME_LINK && (
              <Link
                href="#"
                className="my-3 stack"
                onClick={eve2 => {
                  eve2.preventDefault()
                  setIsLoading(true)
                  setStep(undefined)
                  history.push({
                    pathname: FORGOT_USERNAME_ROUTE,
                    state: { storeToken: storeToken },
                  })
                }}
              >
                Forgot Username
              </Link>
            )}
          </div>
        </div>
      </div>
    </Form>
  )
}

export default UserNamePasswordForm
